import React from "react";
import { FiInbox } from "react-icons/fi";
import AuthInput from "../../../components/Input/AuthInput";
import CustomPagination from "../../../components/TablePagination/TablePagination";
import { fmtReportRs } from "../../../utils/reportSummary";
import { formatDateTime } from "../../../utils/dateTime";

const formatPaymentDate = (row) => {
  const unix = Number(row?.date);
  if (Number.isFinite(unix) && unix > 0) return formatDateTime(unix);
  if (row?.f_date) return formatDateTime(row.f_date);
  return "—";
};

const paymentTypeLabel = (type) => (Number(type) === 2 ? "Bank" : "Cash");

const USER_TYPE_STYLES = {
  Customer: "bg-successSoft text-success border-success/20",
  Company: "bg-subtle text-inkSecondary border-line",
};

const ReportPaymentCard = ({ payment }) => {
  const userType = payment?.user_type || "Customer";
  const typeStyle = USER_TYPE_STYLES[userType] || USER_TYPE_STYLES.Customer;
  const isCash = Number(payment?.payment_type) === 1;

  return (
    <article className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
      <div className="px-4 py-3 border-b bg-subtle border-line">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full border ${typeStyle}`}
          >
            {userType}
          </span>
          <span className="text-xs font-semibold text-muted tabular-nums">
            {formatPaymentDate(payment)}
          </span>
        </div>

        <h3 className="mt-2 text-base font-bold text-ink leading-snug break-words">
          {payment?.name || payment?.customer_name || payment?.company_name || "—"}
        </h3>

        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
          {payment?.invoice_no && (
            <span className="font-semibold text-inkSecondary">
              Inv {payment.invoice_no}
            </span>
          )}
          <span
            className={`rounded-md px-2 py-0.5 font-medium border ${
              isCash
                ? "bg-successSoft text-success border-success/20"
                : "bg-blue-50 text-blue-700 border-blue-100"
            }`}
          >
            {paymentTypeLabel(payment?.payment_type)}
          </span>
        </div>

        {payment?.desc && (
          <p className="mt-2 text-xs text-muted line-clamp-3">{payment.desc}</p>
        )}
      </div>

      <div className="px-4 py-3">
        <p className="text-[10px] uppercase tracking-wide text-muted font-semibold">
          Amount
        </p>
        <p
          className={`text-xl font-extrabold tabular-nums ${
            userType === "Company" ? "text-danger" : "text-success"
          }`}
        >
          {userType === "Company" ? "− " : "+ "}
          {fmtReportRs(payment?.amount)}
        </p>
      </div>
    </article>
  );
};

const ReportPaymentCardList = ({
  payments = [],
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  searchText,
  setSearchText,
  emptyMessage = "No payments found for this date range.",
}) => {
  const activePayments = payments.filter((row) => row.isDeleted !== true);

  const filtered = activePayments.filter((row) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return (
      String(row.name || "").toLowerCase().includes(q) ||
      String(row.customer_name || "").toLowerCase().includes(q) ||
      String(row.company_name || "").toLowerCase().includes(q) ||
      String(row.invoice_no || "").toLowerCase().includes(q) ||
      String(row.desc || "").toLowerCase().includes(q) ||
      String(row.user_type || "").toLowerCase().includes(q)
    );
  });

  const pageRows = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (!activePayments.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/60">
        <div className="w-16 h-16 rounded-2xl bg-brandSoft flex items-center justify-center mb-4">
          <FiInbox className="text-main" size={28} />
        </div>
        <p className="font-bold text-inkSecondary text-lg">No payments</p>
        <p className="text-sm text-muted mt-1 text-center max-w-xs">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-3">
      <AuthInput
        Value={searchText}
        setValue={setSearchText}
        placeholder="Search name, invoice #, description…"
      />

      {filtered.length === 0 ? (
        <div className="w-full text-center text-muted py-10 text-sm rounded-xl border border-dashed border-line bg-subtle/60">
          No matches for your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {pageRows.map((payment) => (
            <ReportPaymentCard key={payment._id || payment.id} payment={payment} />
          ))}
        </div>
      )}

      <CustomPagination
        count={filtered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        RowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </div>
  );
};

export default ReportPaymentCardList;
